import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  addProductAPI,
  getProductsByManagingBranchAPI,
  regionalUpdateProductAPI,
  regionalUpdateProductStatusAPI,
  uploadImageCloudinaryAPI,
} from '../api-services/Products.service';
import { openAiProductDescriptionAPI, openAiProductNameAPI } from '../api-services/OpenAi.services';
import { fetchProductCategoryData } from '../../utils/productCategoryCache';

const KitchenProductsDataContext = createContext(null);

const emptyRequest = () => ({
  loading: false,
  data: null,
  success: null,
  errorMessage: '',
});

const initialProductsList = {
  loading: false,
  data: null,
  success: null,
  errorMessage: '',
};

export function KitchenProductsDataProvider({ children }) {
  const [productsByManagingBranchData, setProductsByManagingBranchData] =
    useState(initialProductsList);
  const [productCategoryData, setProductCategoryData] = useState(emptyRequest());
  const [addProductData, setAddProductData] = useState(emptyRequest());
  const [uploadImageCloudinaryData, setUploadImageCloudinaryData] = useState(emptyRequest());
  const [regionalUpdateProductData, setRegionalUpdateProductData] = useState(emptyRequest());
  const [regionalUpdateProductStatusData, setRegionalUpdateProductStatusData] =
    useState(emptyRequest());
  const [openAiProductNameData, setOpenAiProductNameData] = useState(emptyRequest());
  const [openAiProductDescriptionData, setOpenAiProductDescriptionData] =
    useState(emptyRequest());

  const markProductsByManagingBranchSatisfiedWithoutFetch = useCallback(() => {
    setProductsByManagingBranchData({
      loading: false,
      data: null,
      success: true,
      errorMessage: '',
    });
  }, []);

  const fetchProductsByManagingBranch = useCallback(async (params) => {
    setProductsByManagingBranchData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });
    try {
      const data = await getProductsByManagingBranchAPI(params);
      if (data.hasError) {
        setProductsByManagingBranchData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Failed to load products.',
        });
        return data;
      }
      const payload = data.data || {};
      const ok = payload.success !== false && !data.hasError;
      setProductsByManagingBranchData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok ? '' : payload.message || 'Could not load products.',
      });
      return data;
    } catch (error) {
      console.log('fetchProductsByManagingBranch catch', error);
      setProductsByManagingBranchData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to load products.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const fetchProductCategory = useCallback(async (options = {}) => {
    setProductCategoryData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });
    try {
      const parsed = await fetchProductCategoryData(options);
      if (parsed.ok) {
        setProductCategoryData({
          loading: false,
          data: parsed.data,
          success: true,
          errorMessage: '',
        });
      } else {
        setProductCategoryData({
          loading: false,
          data: null,
          success: false,
          errorMessage: parsed.message || 'Failed to load categories',
        });
      }
      return parsed;
    } catch (error) {
      console.log('fetchProductCategory catch', error);
      setProductCategoryData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to load categories',
      });
      return { ok: false, data: null, message: error?.message };
    }
  }, []);

  const addProduct = useCallback(async (body) => {
    setAddProductData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });
    try {
      const data = await addProductAPI(body);
      if (data?.hasError) {
        setAddProductData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Failed to add product.',
        });
        return data;
      }
      const payload = data?.data ?? {};
      const ok = payload.success === true;
      setAddProductData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok ? '' : payload.CustomMessage || payload.message || 'Failed to add product.',
      });
      return data;
    } catch (error) {
      console.log('addProduct catch', error);
      setAddProductData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to add product.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const uploadImageCloudinary = useCallback(async (formData) => {
    setUploadImageCloudinaryData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });
    try {
      const data = await uploadImageCloudinaryAPI(formData);
      if (data?.hasError) {
        setUploadImageCloudinaryData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Image upload failed.',
        });
        return data;
      }
      const payload = data?.data ?? {};
      const ok = payload.success !== false;
      setUploadImageCloudinaryData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok ? '' : payload.message || 'Image upload failed.',
      });
      return data;
    } catch (error) {
      console.log('uploadImageCloudinary catch', error);
      setUploadImageCloudinaryData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Image upload failed.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const regionalUpdateProduct = useCallback(async (body) => {
    setRegionalUpdateProductData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });
    try {
      const data = await regionalUpdateProductAPI(body);
      if (data?.hasError) {
        setRegionalUpdateProductData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Failed to update product.',
        });
        return data;
      }
      const payload = data?.data ?? {};
      const ok = payload.success === true;
      setRegionalUpdateProductData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok ? '' : payload.message || 'Failed to update product.',
      });
      return data;
    } catch (error) {
      console.log('regionalUpdateProduct catch', error);
      setRegionalUpdateProductData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to update product.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const regionalUpdateProductStatus = useCallback(async (body) => {
    setRegionalUpdateProductStatusData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });
    try {
      const data = await regionalUpdateProductStatusAPI(body);
      if (data?.hasError) {
        setRegionalUpdateProductStatusData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Failed to update product status.',
        });
        return data;
      }
      const payload = data?.data ?? {};
      const ok = payload.success === true;
      setRegionalUpdateProductStatusData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok ? '' : payload.message || 'Failed to update product status.',
      });
      return data;
    } catch (error) {
      console.log('regionalUpdateProductStatus catch', error);
      setRegionalUpdateProductStatusData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to update product status.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const openAiProductName = useCallback(async (payload = {}) => {
    setOpenAiProductNameData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });
    try {
      const data = await openAiProductNameAPI(payload);
      if (data?.hasError) {
        setOpenAiProductNameData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Request failed',
        });
        return data;
      }
      setOpenAiProductNameData({
        loading: false,
        data,
        success: true,
        errorMessage: '',
      });
      return data;
    } catch (error) {
      console.log('openAiProductName catch', error);
      setOpenAiProductNameData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Request failed',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const openAiProductDescription = useCallback(async (payload = {}) => {
    setOpenAiProductDescriptionData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });
    try {
      const data = await openAiProductDescriptionAPI(payload);
      if (data?.hasError) {
        setOpenAiProductDescriptionData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Request failed',
        });
        return data;
      }
      setOpenAiProductDescriptionData({
        loading: false,
        data,
        success: true,
        errorMessage: '',
      });
      return data;
    } catch (error) {
      console.log('openAiProductDescription catch', error);
      setOpenAiProductDescriptionData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Request failed',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const value = useMemo(
    () => ({
      productsByManagingBranchData,
      fetchProductsByManagingBranch,
      markProductsByManagingBranchSatisfiedWithoutFetch,
      productCategoryData,
      fetchProductCategory,
      addProductData,
      addProduct,
      uploadImageCloudinaryData,
      uploadImageCloudinary,
      regionalUpdateProductData,
      regionalUpdateProduct,
      regionalUpdateProductStatusData,
      regionalUpdateProductStatus,
      openAiProductNameData,
      openAiProductName,
      openAiProductDescriptionData,
      openAiProductDescription,
    }),
    [
      productsByManagingBranchData,
      fetchProductsByManagingBranch,
      markProductsByManagingBranchSatisfiedWithoutFetch,
      productCategoryData,
      fetchProductCategory,
      addProductData,
      addProduct,
      uploadImageCloudinaryData,
      uploadImageCloudinary,
      regionalUpdateProductData,
      regionalUpdateProduct,
      regionalUpdateProductStatusData,
      regionalUpdateProductStatus,
      openAiProductNameData,
      openAiProductName,
      openAiProductDescriptionData,
      openAiProductDescription,
    ]
  );

  return (
    <KitchenProductsDataContext.Provider value={value}>{children}</KitchenProductsDataContext.Provider>
  );
}

export function useKitchenProductsData() {
  const ctx = useContext(KitchenProductsDataContext);
  if (ctx == null) {
    throw new Error('useKitchenProductsData must be used within KitchenProductsDataProvider');
  }
  return ctx;
}
